import React from "react";
import moment from "moment";
import cns from "classnames";
import { isEqual } from "lodash";
import { Observer, useObserver } from "mobx-react";
import { Button, Collapse, Grid, TextField, withStyles } from "@material-ui/core";
import { Classes, ThemeType } from "@theme";
import {
  ConsultantStatus,
  InitialContact,
  LastContactType,
  NodeType,
  OrganizationType,
  useGetUserSelectUsersQuery,
  Workshift
} from "@graphql";
import { DateRange } from "@components/Range/DateRange";
import Switch from "@components/forms/common/Switch";
import {
  Grid3,
  renderAOESelector,
  renderConsultantSelector,
  renderCustomerTypeSelect,
  renderFrom,
  renderOrgTypeSelect,
  renderRegions,
  renderSpecialization,
  renderTo,
  renderUsers,
  SearchSelect
} from "../filters";
import { CallListSelector } from "../filters";
import Searchbar from "../Searchbar";
import {
  GeneralSearchStore,
  initialSearchParams as initial
} from "../GeneralSearchStore";
import { fitnessFilterOptions } from "@bonliva-crm/ui/search-filters";
import { useLocale } from "@hooks";

export type Children = (props: {
  areasOfExpertise: JSX.Element;
  availableForWorkInCountries: JSX.Element;
  callLists: JSX.Element;
  competitors: JSX.Element;
  customerType: JSX.Element;
  customerNumbers: JSX.Element;
  expertiseWithExperienceAndExclusive: JSX.Element;
  initialContact: JSX.Element;
  fitness: JSX.Element;
  from: JSX.Element;
  knownSystems: JSX.Element;
  lastContact: JSX.Element;
  lastContactType: JSX.Element;
  organizationType: JSX.Element;
  qualification: JSX.Element;
  region: JSX.Element;
  responsiblePerson: JSX.Element;
  seniorAndDrivingLicense: JSX.Element;
  specializations: JSX.Element;
  status: JSX.Element;
  to: JSX.Element;
  workshifts: JSX.Element;
  journalSystem: JSX.Element;
  consultants: JSX.Element;
}) => JSX.Element | [JSX.Element, JSX.Element];

export type FormProps = {
  children: Children;
  nodeType: NodeType;
  // callListType: NodeType;
  store?: GeneralSearchStore;
  hideSearchBar?: boolean;
};

type Props = FormProps & {
  classes: Classes<typeof styles>;
};

export const SearchForm: React.FC<Props> = (props) => {
  const { classes, store, nodeType } = props;
  const { searchParams: args } = store;
  const { locale } = useLocale();

  const { data: usersData } = useGetUserSelectUsersQuery();

  const onExpertiseChange = (value: string[]) => {
    const currentSpecs = args.specializations;
    const validSpecs = currentSpecs.filter((v) => store.allSpecializations.includes(v));

    if (!isEqual(currentSpecs, validSpecs)) {
      args.specializations = validSpecs;
    }
    args.areasOfExpertiseProfessions = value;
  };

  const areasOfExpertise = (
    <Grid3
      dirty={
        !isEqual(args.areasOfExpertiseProfessions, initial.areasOfExpertiseProfessions) ||
        args.areasOfExpertiseExperience !== initial.areasOfExpertiseExperience
      }
    >
      {renderAOESelector(
        onExpertiseChange,
        args.areasOfExpertiseProfessions,
        !args.areasOfExpertiseProfessions,
        false
      )}
    </Grid3>
  );

  const callLists = (
    <CallListSelector
      nodeType={nodeType}
      value={args.callListIds}
      onChange={(v) => (args.callListIds = v)}
      dirty={!isEqual(args.callListIds, initial.callListIds)}
    />
  );

  const expertiseWithExperienceAndExclusive = (
    <Grid3
      dirty={
        !isEqual(args.areasOfExpertiseProfessions, initial.areasOfExpertiseProfessions) ||
        args.areasOfExpertiseExperience !== initial.areasOfExpertiseExperience
      }
    >
      <div className={classes.areasOfExpertiseProfessions}>
        {renderAOESelector(
          onExpertiseChange,
          args.areasOfExpertiseProfessions,
          !args.areasOfExpertiseProfessions,
          true
        )}
      </div>
    </Grid3>
  );

  const organizationType = renderOrgTypeSelect(
    args.organizationType as OrganizationType[],
    (v) => (args.organizationType = v),
    false,
    !isEqual(args.organizationType, initial.organizationType)
  );

  const initialContactValues = Object.values(InitialContact).toLabelValue(
    (v) => locale.initialContact[v]
  );

  const initialContact = (
    <Grid3 dirty={!isEqual(args.initialContact, initial.initialContact)}>
      <SearchSelect
        label={locale.InitialContact}
        value={args.initialContact}
        onChange={(v) => (args.initialContact = v)}
        options={initialContactValues}
      />
    </Grid3>
  );

  const specializations = renderSpecialization(
    args.specializations,
    (v) => (args.specializations = v),
    !isEqual(args.specializations, initial.specializations),
    !args.areasOfExpertiseProfessions
  );

  const responsiblePerson = renderUsers(
    usersData?.users || [],
    locale["Responsible BA"],
    args.responsiblePerson,
    (v) => (args.responsiblePerson = v),
    !isEqual(args.responsiblePerson, initial.responsiblePerson),
    false,
    true,
    true
  );

  const lastContact = (
    <Grid3 dirty={args.myActivity || !isEqual(args.lastContact, initial.lastContact)}>
      <DateRange
        value={args.lastContact}
        onChange={(v) => (args.lastContact = v)}
        label={
          <div className={cns(classes.myActivity, classes.rangeLabel)}>
            <span>{locale.SelectSomething(locale.LastContact)}</span>
            <Switch
              label={locale.MyActivity}
              value={args.myActivity}
              onChange={(v) => (args.myActivity = v)}
              switchProps={{ size: "small" }}
            />
          </div>
        }
      />
    </Grid3>
  );

  const lastContactType = (
    <Grid3 dirty={!isEqual(args.lastContactType, initial.lastContactType)}>
      <SearchSelect
        label={locale.LastContact}
        options={Object.values(LastContactType)
          .filter((t) => t !== LastContactType.MetInPerson)
          .toLabelValue((v) => locale.lastContactType[v])}
        value={args.lastContactType}
        onChange={(v) => (args.lastContactType = v)}
      />
    </Grid3>
  );

  const competitors = (
    <Grid3 dirty={!isEqual(args.competitors, initial.competitors)}>
      <SearchSelect
        label={locale["Works via competitors"]}
        options={store.allCompetitors}
        onAllSelect={(opt) =>
          (args.competitors = args.competitors.concat(opt.map((o) => o.value)).unique())
        }
        value={args.competitors}
        onChange={(v) => {
          return (args.competitors = v);
        }}
      />
    </Grid3>
  );

  const region = renderRegions(
    args.regions,
    (v) => (args.regions = v),
    !isEqual(args.regions, initial.regions)
  );

  const customerType = renderCustomerTypeSelect(
    args.customerType,
    (v) => (args.customerType = v),
    false,
    !isEqual(args.customerType, initial.customerType)
  );

  const from = renderFrom(
    args.from,
    (v) => (args.from = v),
    moment(args.from).isSame(initial.from),
    args.to
  );

  const to = renderTo(
    args.to,
    (v) => (args.to = v),
    moment(args.to).isSame(initial.to),
    args.from
  );

  const qualificationOptions = [
    { label: locale.ShowAll, value: "all" },
    { label: locale.ShowQualified, value: "showQualified" },
    { label: locale.ShowUnqualified, value: "showUnqualified" }
  ];

  const qualification = (
    <Grid3 dirty={args.qualification !== initial.qualification}>
      <SearchSelect
        label={locale.QualificationFilter}
        options={qualificationOptions}
        value={args.qualification}
        onChange={(v) => (args.qualification = v || qualificationOptions[0])}
        sortOptions={false}
      />
    </Grid3>
  );

  const seniorControl = (
    <Switch
      label={locale.Senior}
      value={args.senior}
      onChange={(v) => (args.senior = v)}
      switchProps={{
        size: "small"
      }}
    />
  );

  const hasDrivingLicenceControl = (
    <Switch
      label={locale.HasDrivingLicence}
      value={args.hasDrivingLicence}
      onChange={(v) => (args.hasDrivingLicence = v)}
      switchProps={{
        size: "small"
      }}
    />
  );

  const seniorAndDrivingLicense = (
    <Grid3
      dirty={
        args.hasDrivingLicence !== initial.hasDrivingLicence ||
        args.senior !== initial.senior
      }
    >
      {seniorControl}
      {hasDrivingLicenceControl}
    </Grid3>
  );

  const status = (
    <Grid3 dirty={!isEqual(args.status, initial.status)}>
      <SearchSelect
        label={locale.Status}
        options={Object.values(ConsultantStatus).map((s) => ({
          label: locale.consultantStatus[s],
          value: s
        }))}
        value={args.status}
        onChange={(v) => (args.status = v)}
        testName="status"
      />
    </Grid3>
  );

  const fitness = (
    <Grid3 dirty={!isEqual(args.fitness, initial.fitness)}>
      <SearchSelect
        label={locale.Fitness.toLowerCase()}
        options={fitnessFilterOptions}
        value={args.fitness}
        onChange={(v) => (args.fitness = v)}
      />
    </Grid3>
  );

  const availableForWorkInCountries = (
    <Grid3
      dirty={
        !isEqual(args.availableForWorkInCountries, initial.availableForWorkInCountries)
      }
    >
      <SearchSelect
        label={locale["Can work in"]}
        options={store.countries.toLabelValue(locale.getCountryName)}
        value={args.availableForWorkInCountries}
        onChange={(v) => (args.availableForWorkInCountries = v)}
      />
    </Grid3>
  );

  const knownSystems = (
    <Grid3 dirty={!isEqual(args.knownSystems, initial.knownSystems)}>
      <SearchSelect
        label={locale["Known systems"]}
        options={store.knownSystems}
        value={args.knownSystems}
        onChange={(v) => (args.knownSystems = v)}
      />
    </Grid3>
  );

  const workshifts = (
    <Grid3 dirty={!isEqual(args.workshifts, initial.workshifts)}>
      <SearchSelect
        label={locale.PreferredSchedule}
        options={Object.values(Workshift).toLabelValue((w) => locale.workshifts[w])}
        value={args.workshifts}
        onChange={(v) => (args.workshifts = v)}
      />
    </Grid3>
  );

  const searchBar = (
    <Observer>
      {() => (
        <Searchbar
          className={classes.searchbar}
          onChange={(v) => {
            if (nodeType === NodeType.Customer) {
              store.sortOption = v ? "relevance" : "name_asc";
            }

            store.setSearchParams({ text: v });
          }}
          searchType={args.searchType}
          // withSearchType
          onSearchTypeChange={(v) => (args.searchType = v)}
          value={args.text}
        />
      )}
    </Observer>
  );

  const customerNumbers = (
    <Grid3 dirty={!isEqual(args.customerNumbers, initial.customerNumbers)}>
      <TextField
        label={locale.CustomerNumberShort}
        value={args.customerNumbers}
        onChange={(v) => (args.customerNumbers = v.currentTarget.value)}
        fullWidth
      />
    </Grid3>
  );

  const journalSystem = (
    <Grid3 dirty={!isEqual(args.journalSystem, initial.journalSystem)}>
      <SearchSelect
        label={locale.journalSystem}
        options={store.journalSystems.toLabelValue()}
        value={args.journalSystem}
        onChange={(v) => (args.journalSystem = v)}
      />
    </Grid3>
  );

  const consultants = renderConsultantSelector(
    (v) => {
      args.consultants = v.toLabelValue(
        (x) => x.Name,
        (x) => x._id
      );
    },
    args.consultants,
    !isEqual(args.consultants, initial.consultants)
  );

  const formElements = props.children({
    areasOfExpertise,
    availableForWorkInCountries,
    callLists,
    competitors,
    customerType,
    customerNumbers,
    expertiseWithExperienceAndExclusive,
    initialContact,
    fitness,
    from,
    knownSystems,
    lastContact,
    lastContactType,
    organizationType,
    qualification,
    region,
    responsiblePerson,
    seniorAndDrivingLicense,
    specializations,
    status,
    to,
    workshifts,
    journalSystem,
    consultants
  });

  const mainFields = Array.isArray(formElements) ? formElements[0] : formElements;
  const extraFields = Array.isArray(formElements) ? formElements[1] : null;

  return useObserver(() => (
    <div>
      <form id="search_page_search_form" data-test="SearchForm">
        <div className={classes.root}>
          {props.hideSearchBar ? null : searchBar}
          <Grid container spacing={1} className={classes.main}>
            {mainFields}
          </Grid>

          {extraFields && (
            <>
              <Collapse
                in={store.isToggled}
                onEntered={(node) => (node.style.overflow = "visible")}
                onExit={(node) => (node.style.overflow = "hidden")}
              >
                <Grid container spacing={1} className={classes.extended}>
                  {extraFields}
                </Grid>
              </Collapse>

              <div
                className={cns(
                  { [classes.moreDisableInSticky]: store.isMoreDisabledInSticky },
                  classes.more
                )}
              >
                <Button
                  fullWidth
                  variant="text"
                  onClick={store.toggleForm}
                  data-test="SearchFormExpand"
                >
                  {store.isToggled ? locale.Less : locale.More}
                </Button>
              </div>
            </>
          )}
        </div>
      </form>
    </div>
  ));
};

export const styles = (theme: ThemeType) => ({
  root: {
    ...theme.fixedGrid,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    position: "relative"
  },

  searchbar: {
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },

  main: {
    alignItems: "flex-end",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    marginTop: theme.spacing(2),
    width: "100%",
    margin: 0,
    zIndex: 2,

    "&:last-child": {
      paddingBottom: theme.spacing(2)
    }
  },

  extended: {
    alignItems: "flex-end",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    width: "100%",
    margin: 0,
    zIndex: 2
  },

  more: {
    marginTop: theme.spacing(),
    width: "100%",
    position: "absolute",
    bottom: -54,

    "& button": {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0
    }
  },

  moreDisableInSticky: {
    ".StickyContainer__pulldown &": {
      display: "none !important"
    }
  },

  zipCityLocation: {
    alignItems: "flex-end",
    display: "flex",

    "& > div:first-child": {
      width: "63%",
      marginRight: "5px"
    },

    "& > div:last-child": {
      width: "27%"
    }
  },

  areasOfExpertiseProfessions: {
    alignItems: "flex-end",
    display: "flex"

    // "& > div:first-child": {
    //   flex: "1 0 0",
    //   marginRight: theme.spacing()
    // },

    // "& > div:last-child": {
    //   flex: "0 0 64px"
    // }
  },

  rangeLabel: {
    width: "120%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },

  myActivity: {
    "& > label": {
      flex: "1 1",
      marginLeft: 0
    }
  }
});

export default withStyles(styles as any)(SearchForm as any);
